import { useEffect, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { registerServiceWorker, activateWaitingWorker } from '@/pwa/registerServiceWorker'

/**
 * Bottom-of-screen toast that appears when a new app version has been cached
 * and is waiting to take over. Click "Update now" to skip-waiting and reload.
 *
 * Lives at the AppShell level so it overlays every module without coupling
 * to any single one.
 */
export function PWAUpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    registerServiceWorker({
      onUpdateReady: (reg) => setRegistration(reg),
    })
  }, [])

  if (!registration || dismissed) return null

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-[calc(100vw-2rem)]">
      <div className="flex items-center gap-3 bg-emerald-900/90 border border-emerald-700 rounded-xl px-4 py-3 shadow-2xl backdrop-blur-sm">
        <RefreshCw className="w-4 h-4 text-emerald-300 shrink-0" />
        <div className="text-sm text-emerald-100 min-w-0">
          <div className="font-medium">New version available</div>
          <div className="text-xs text-emerald-200/80 mt-0.5">
            Reload to get the latest features and fixes.
          </div>
        </div>
        <button
          onClick={() => activateWaitingWorker(registration)}
          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-lg text-xs font-semibold transition-colors shrink-0"
        >
          Update now
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="text-emerald-300 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
